import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Check, AlertCircle, Loader2 } from 'lucide-react';
import { submitSignupForm } from './api';
import { useClarityModalTracking } from '../../../hooks/useClarityModalTracking';
import { trackFormInteraction, trackFormAbandonment } from '../../../config/clarity';
import {
  validateEmail,
  formatAndValidateSACellphone,
  validateSAIDNumber,
  validateSAPostalCode,
  validateSABankAccount,
  validateSABranchCode
} from './validationUtils';

// Import step components
import CustomerInfoStep from './CustomerInfoStep';
import InstallationStep from './InstallationStep';
import PaymentStep from './PaymentStep';

const SignupModal = ({
  isOpen,
  onClose,
  packageData,
  installationAddress,
  providerName,
  // coordinates, // Remove coordinates prop
  isSideBySideLayout = false,
  formData = {},
  updateFormData = () => {},
  onSubmit = () => {},
  isSubmitting = false
}) => {
  // Add refs for tracking
  const prevIsOpenRef = useRef(isOpen);
  const modalOpenTimeRef = useRef(0);
  const formSubmittedRef = useRef(false);
  
  // Track modal open/close
  const { trackStep, trackAction } = useClarityModalTracking('signup_modal', isOpen, {
    packageName: packageData?.name,
    packageSpeed: packageData?.speed,
    packagePrice: packageData?.price
  });
  
  // Mobile detection
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 768);
  
  // Check if we're on mobile
  const isMobile = windowWidth < 768;
  
  // Handle window resize
  const handleResize = useCallback(() => {
    setWindowWidth(window.innerWidth);
  }, []);
  
  // Set up resize listener
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;
  
  // Form data is now passed as a prop
  
  // Form state
  const [errors, setErrors] = useState({});
  const [showErrorSummary, setShowErrorSummary] = useState(false);
  const [errorSummary, setErrorSummary] = useState([]);
  // Use the isSubmitting prop from parent instead of local state
  // Get submitResult from formData if available, otherwise use local state
  const [localSubmitResult, setLocalSubmitResult] = useState(null);
  // Use formData.submitResult if available, otherwise use local state
  const submitResult = formData.submitResult || localSubmitResult;
  
  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setErrors({});
    }
  }, [isOpen]);
  
  // Track step changes
  useEffect(() => {
    if (isOpen && currentStep > 0) {
      trackStep(`step_${currentStep}`, {
        stepName: currentStep === 1 ? 'customer_info' :
                 currentStep === 2 ? 'installation' : 'payment'
      });
    }
  }, [currentStep, isOpen, trackStep]);
  
  // Validate current step
  const validateStep = () => {
    let stepErrors = {};
    let isValid = true;
    
    if (currentStep === 1) {
      // Customer Info validation
      if (!formData.title) {
        stepErrors.title = 'Please select a title';
        isValid = false;
      }
      
      if (!formData.firstName) {
        stepErrors.firstName = 'First name is required';
        isValid = false;
      }
      
      if (!formData.surname) {
        stepErrors.surname = 'Surname is required';
        isValid = false;
      }
      
      if (!formData.email) {
        stepErrors.email = 'Email is required';
        isValid = false;
      } else {
        const emailResult = validateEmail(formData.email);
        if (!emailResult.valid) {
          stepErrors.email = emailResult.message;
          isValid = false;
        }
      }
      
      if (!formData.cellphone) {
        stepErrors.cellphone = 'Cellphone number is required';
        isValid = false;
      } else {
        const cellphoneResult = formatAndValidateSACellphone(formData.cellphone);
        if (!cellphoneResult.valid) {
          stepErrors.cellphone = cellphoneResult.message;
          isValid = false;
        }
      }
      
      if (!formData.idNumber) {
        stepErrors.idNumber = 'ID number is required';
        isValid = false;
      } else {
        const idResult = validateSAIDNumber(formData.idNumber, formData.title);
        if (!idResult.valid) {
          stepErrors.idNumber = idResult.message;
          isValid = false;
        } else if (idResult.genderMismatch) {
          stepErrors.idNumber = idResult.genderMismatchMessage;
          isValid = false;
        }
      }
      
      if (!formData.existingCustomer) {
        stepErrors.existingCustomer = 'Please select an option';
        isValid = false;
      }
    } 
    else if (currentStep === 2) {
      // Installation validation
      if (!formData.routerOption) {
        stepErrors.routerOption = 'Please select a router option';
        isValid = false;
      }
      
      if (!formData.installationStatus) {
        stepErrors.installationStatus = 'Please select your installation status';
        isValid = false;
      }
      
      // Validate installation date if installation status is selected
      if (formData.installationStatus && !formData.installationDate) {
        stepErrors.installationDate = 'Please select an installation/activation date';
        isValid = false;
      }
      
      // For new installations, check if the tooltip has been acknowledged
      // This is tracked in the component state, so we need to add a hidden field to the form data
      if (formData.installationStatus === 'newInstallation' && formData.tooltipAcknowledged !== true) {
        stepErrors.tooltipAcknowledged = 'You must acknowledge the installation notice before proceeding';
        isValid = false;
      }
      
      // Street number, street name, suburb, and city are pre-filled from coverage check and read-only
      // No validation needed for these fields
      
      if (!formData.postalCode) {
        stepErrors.postalCode = 'Postal code is required';
        isValid = false;
      } else {
        const postalResult = validateSAPostalCode(formData.postalCode);
        if (!postalResult.valid) {
          stepErrors.postalCode = postalResult.message;
          isValid = false;
        }
      }
      
      if (!formData.propertyType) {
        stepErrors.propertyType = 'Please select your property type';
        isValid = false;
      }
      
      // Validate complex/estate fields if property type is complex
      if (formData.propertyType === 'complex') {
        if (!formData.complex) {
          stepErrors.complex = 'Complex/Estate/Apartment name is required';
          isValid = false;
        }
        
        if (!formData.unitNumber) {
          stepErrors.unitNumber = 'Unit number is required';
          isValid = false;
        }
      }
      
      if (!formData.propertyOwnership) {
        stepErrors.propertyOwnership = 'Please select your property ownership';
        isValid = false;
      }
      
      // Validate landlord details if property is rented
      if (formData.propertyOwnership === 'rented') {
        if (!formData.landlordName) {
          stepErrors.landlordName = 'Landlord name is required';
          isValid = false;
        }
        
        if (!formData.landlordContact) {
          stepErrors.landlordContact = 'Landlord contact number is required';
          isValid = false;
        } else {
          const contactResult = formatAndValidateSACellphone(formData.landlordContact);
          if (!contactResult.valid) {
            stepErrors.landlordContact = contactResult.message;
            isValid = false;
          }
        }
        
        if (formData.landlordEmail) {
          const emailResult = validateEmail(formData.landlordEmail);
          if (!emailResult.valid) {
            stepErrors.landlordEmail = emailResult.message;
            isValid = false;
          }
        }
      }
    } 
    else if (currentStep === 3) {
      // Payment validation
      if (!formData.accountNumber) {
        stepErrors.accountNumber = 'Account number is required';
        isValid = false;
      } else if (formData.bank) {
        const accountResult = validateSABankAccount(formData.accountNumber, formData.bank);
        if (!accountResult.valid) {
          stepErrors.accountNumber = accountResult.message;
          isValid = false;
        }
      }
      
      if (!formData.accountHolderName) {
        stepErrors.accountHolderName = 'Account holder name is required';
        isValid = false;
      }
      
      if (!formData.bank) {
        stepErrors.bank = 'Please select your bank';
        isValid = false;
      }
      
      if (!formData.branchName) {
        stepErrors.branchName = 'Branch name is required';
        isValid = false;
      }
      
      if (!formData.branchCode) {
        stepErrors.branchCode = 'Branch code is required';
        isValid = false;
      } else if (formData.bank) {
        const branchResult = validateSABranchCode(formData.branchCode, formData.bank);
        if (!branchResult.valid) {
          stepErrors.branchCode = branchResult.message;
          isValid = false;
        }
      }
      
      if (!formData.accountType) {
        stepErrors.accountType = 'Please select your account type';
        isValid = false;
      }
      
      if (!formData.authoriseDebitOrder) {
        stepErrors.authoriseDebitOrder = 'You must authorise the debit order to proceed';
        isValid = false;
      }
      
      if (!formData.agreeToTerms) {
        stepErrors.agreeToTerms = 'You must agree to the terms and conditions to proceed';
        isValid = false;
      }
      
      // Simplified signature validation
      if (!formData.signature) {
        stepErrors.signature = 'Your signature is required';
        isValid = false;
      }
      
      // Log signature data for debugging
      console.log('Signature data type:', typeof formData.signature);
      console.log('Signature data present:', formData.signature ? 'Yes' : 'No');
    }
    
    // Create error summary for display
    if (!isValid) {
      const summary = Object.entries(stepErrors).map(([field, message]) => ({
        field,
        message,
        step: currentStep
      }));
      setErrorSummary(summary);
      setShowErrorSummary(true);
      
      // Schedule scrolling to the first error
      setTimeout(() => {
        const firstErrorElement = document.getElementById(Object.keys(stepErrors)[0]);
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstErrorElement.focus();
        }
      }, 100);
    } else {
      setShowErrorSummary(false);
    }
    
    setErrors(stepErrors);
    return isValid;
  };
  
  // Handle next step
  const handleNextStep = () => {
    if (validateStep()) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
        // Scroll to top when changing steps
        window.scrollTo(0, 0);
      } else {
        // Submit the form if on the last step
        handleSubmit();
      }
    }
  };
  
  // Handle previous step
  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      // Scroll to top when changing steps
      window.scrollTo(0, 0);
    }
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (validateStep()) {
      try {
        // Reset submit result
        setLocalSubmitResult(null);
        
        // Prepare the complete form data
        const completeFormData = {
          ...formData,
          packageData,
          providerName,
          installationAddress
          // coordinates // Remove coordinates from here
        };

        // ----> ADD THIS LOG <----
        console.log('Data being sent to submitSignupForm:', JSON.stringify(completeFormData, null, 2));
        console.log('Inspecting installationAddress before submit:', installationAddress);
        // console.log('Inspecting coordinates before submit:', coordinates); // Remove log
        // ------------------------
        
        // Track form submission
        trackFormInteraction('signup_form', 'submit', {
          packageName: packageData?.name,
          steps_completed: currentStep
        });
        
        // Mark form as submitted
        formSubmittedRef.current = true;
        
        // Call the onSubmit prop which will handle the API call and loading state
        onSubmit(completeFormData);
      } catch (error) {
        console.error('Error in handleSubmit:', error);
        setLocalSubmitResult({
          success: false,
          message: error.message || 'An unexpected error occurred'
        });
      }
    }
  };
  
  // Track form abandonment
  useEffect(() => {
    // Only track abandonment if the modal was open and is now closing
    if (!isOpen && prevIsOpenRef.current) {
      // Don't track abandonment if the form was successfully submitted
      if (!formSubmittedRef.current) {
        trackFormAbandonment('signup_form', `step_${currentStep}`, 'modal_closed', {
          packageName: packageData?.name,
          timeSpentSeconds: (Date.now() - modalOpenTimeRef.current) / 1000
        });
      }
    }
    
    // Update previous state
    prevIsOpenRef.current = isOpen;
    
    // Track when modal opens
    if (isOpen && !prevIsOpenRef.current) {
      modalOpenTimeRef.current = Date.now();
      formSubmittedRef.current = false;
    }
  }, [isOpen, currentStep, packageData]);
  
  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.3, delay: 0.1 }
    }
  };
  
  const modalVariants = {
    hidden: { 
      opacity: 0
    },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    },
    exit: { 
      opacity: 0,
      transition: { 
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  
  // Render the current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <CustomerInfoStep 
            formData={formData} 
            updateFormData={updateFormData} 
            errors={errors}
          />
        );
      case 2:
        return (
          <InstallationStep 
            formData={formData} 
            updateFormData={updateFormData} 
            installationAddress={installationAddress}
            // coordinates={coordinates} // Remove prop pass-down
            errors={errors}
          />
        );
      case 3:
        return (
          <PaymentStep 
            formData={formData} 
            updateFormData={updateFormData} 
            errors={errors}
          />
        );
      default:
        return null;
    }
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <div className={`fixed inset-0 z-40 overflow-hidden ${isSideBySideLayout && !isMobile ? 'left-1/4 right-0 w-auto' : ''}`}>
          {/* Semi-transparent Backdrop that doesn't cover the navigation or sidebar */}
          {!isSideBySideLayout && (
            <motion.div
              className="fixed inset-0 top-16 bg-gray-900/90 backdrop-blur-sm"
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            />
          )}
          
          {/* Modal that starts below the navigation and doesn't overlap sidebar */}
          <motion.div
            className={`fixed top-16 bottom-0 flex flex-col ${isSideBySideLayout && !isMobile ? 'left-1/4 right-0' : 'inset-x-0'}`}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{ zIndex: 30 }} /* Ensure it's below the SignupSummary z-index of 40 */
          >
            <div className="flex flex-col h-full w-full bg-gray-900">
              {/* Header */}
              <div className="p-3 md:p-6 border-b border-gray-800 flex justify-between items-center bg-gradient-to-r from-gray-900 to-gray-800">
                <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-white flex items-center`}>
                  Sign Up for {providerName || 'Fibre'} Package
                </h2>
                <button
                  className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-800/50 transition-colors"
                  onClick={onClose}
                >
                  <X className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'}`} />
                </button>
              </div>
              
              {/* Progress Steps */}
              <div className="bg-gray-800/50 px-3 md:px-6 py-3 md:py-4 border-b border-gray-800">
                <div className="flex items-center justify-between">
                  {/* Step 1 */}
                  <div className="flex flex-col items-center">
                    <div className={`${isMobile ? 'w-7 h-7' : 'w-8 h-8'} rounded-full flex items-center justify-center ${
                      currentStep === 1
                        ? 'bg-purple-600 text-white'
                        : currentStep > 1
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-700 text-gray-400'
                    }`}>
                      {currentStep > 1 ? <Check className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} /> : 1}
                    </div>
                    <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} mt-1 ${
                      currentStep >= 1 ? 'text-white' : 'text-gray-500'
                    }`}>Customer Info</span>
                  </div>
                  
                  {/* Connector */}
                  <div className={`h-1 flex-1 mx-2 ${
                    currentStep > 1 ? 'bg-green-500' : 'bg-gray-700'
                  }`}></div>
                  
                  {/* Step 2 */}
                  <div className="flex flex-col items-center">
                    <div className={`${isMobile ? 'w-7 h-7' : 'w-8 h-8'} rounded-full flex items-center justify-center ${
                      currentStep === 2
                        ? 'bg-purple-600 text-white'
                        : currentStep > 2
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-700 text-gray-400'
                    }`}>
                      {currentStep > 2 ? <Check className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} /> : 2}
                    </div>
                    <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} mt-1 ${
                      currentStep >= 2 ? 'text-white' : 'text-gray-500'
                    }`}>Installation</span>
                  </div>
                  
                  {/* Connector */}
                  <div className={`h-1 flex-1 mx-2 ${
                    currentStep > 2 ? 'bg-green-500' : 'bg-gray-700'
                  }`}></div>
                  
                  {/* Step 3 */}
                  <div className="flex flex-col items-center">
                    <div className={`${isMobile ? 'w-7 h-7' : 'w-8 h-8'} rounded-full flex items-center justify-center ${
                      currentStep === 3
                        ? 'bg-purple-600 text-white'
                        : currentStep > 3
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-700 text-gray-400'
                    }`}>
                      3
                    </div>
                    <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} mt-1 ${
                      currentStep >= 3 ? 'text-white' : 'text-gray-500'
                    }`}>Payment</span>
                  </div>
                </div>
              </div>
              
              {/* Form Content */}
              <div className="flex-1 overflow-y-auto p-3 md:p-6">
                {/* Success Message - Show prominently when available */}
                {submitResult && submitResult.success && (
                  <div className="mb-6 p-6 bg-green-900/30 border border-green-800/30 rounded-lg">
                    <div className="flex items-start">
                      <Check className="h-6 w-6 text-green-400 mr-3 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-xl font-bold text-green-400 mb-2">
                          Application Submitted Successfully!
                        </h3>
                        <p className="text-green-300 mb-4">
                          {submitResult.message}
                        </p>
                        
                        {/* Display reference number prominently */}
                        {submitResult.data && submitResult.data.referenceNumber && (
                          <div className="mt-4 p-4 bg-gray-800 rounded-md border border-green-500/30">
                            <p className="text-gray-300 text-sm mb-2">Your order reference number:</p>
                            <p className="text-2xl font-mono font-bold text-white select-all mb-2">
                              {submitResult.data.referenceNumber}
                            </p>
                            <p className="text-gray-400 text-sm mt-2">
                              Please save this reference number for future inquiries about your order.
                            </p>
                          </div>
                        )}
                        
                        <p className="text-gray-300 mt-4">
                          Our team will contact you within 24-48 hours to confirm your order details and schedule your installation.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Error Summary */}
                {showErrorSummary && errorSummary.length > 0 && (
                  <div className="mb-6 p-4 bg-red-900/30 border border-red-800/30 rounded-lg">
                    <h3 className="text-red-400 font-medium mb-2">Please fix the following errors:</h3>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-red-300">
                      {errorSummary.map((error, index) => (
                        <li key={index}>
                          <button
                            className="text-left hover:text-red-200 underline"
                            onClick={() => {
                              const element = document.getElementById(error.field);
                              if (element) {
                                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                element.focus();
                              }
                            }}
                          >
                            {error.message}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Error Message */}
                {submitResult && !submitResult.success && (
                  <div className="mb-6 p-4 bg-red-900/30 border border-red-800/30 rounded-lg">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-medium text-red-400">
                          Error
                        </h3>
                        <p className="text-sm text-red-300">
                          {submitResult.message}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Only render the step form if we don't have a successful submission */}
                {(!submitResult || !submitResult.success) && renderStep()}
              </div>
              
              {/* Navigation Buttons */}
              <div className="p-3 md:p-6 border-t border-gray-800 flex justify-between">
                {/* Show a single "Close" button if submission was successful */}
                {submitResult && submitResult.success ? (
                  <button
                    className="w-full px-3 md:px-4 py-3 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white rounded-lg font-medium transition-colors"
                    onClick={onClose}
                  >
                    Close
                  </button>
                ) : (
                  <>
                    <button
                      className={`px-3 md:px-4 py-2 rounded-lg ${isMobile ? 'text-xs' : 'text-sm'} font-medium ${
                        currentStep === 1
                          ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-700 text-white hover:bg-gray-600 transition-colors'
                      }`}
                      onClick={handlePrevStep}
                      disabled={currentStep === 1}
                    >
                      <div className="flex items-center">
                        <ChevronLeft className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
                        Previous
                      </div>
                    </button>
                    
                    <button
                      className={`px-3 md:px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg ${isMobile ? 'text-xs' : 'text-sm'} font-medium transition-colors ${
                        isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                      }`}
                      onClick={handleNextStep}
                      disabled={isSubmitting}
                    >
                      <div className="flex items-center">
                        {isSubmitting ? (
                          <>
                            <Loader2 className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} mr-2 animate-spin`} />
                            Processing...
                          </>
                        ) : currentStep < totalSteps ? (
                          <>
                            Next
                            <ChevronRight className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} ml-1`} />
                          </>
                        ) : (
                          'Submit Application'
                        )}
                      </div>
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SignupModal;